import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {fetchCityProfile, upsertCityProfile} from '../endpoints/api';
import useAuth from './useAuth';

export interface CityProfileData {
  city_duration: string | null;
  neighborhood_duration: string | null;
  home_duration: string | null;
  is_renting: boolean | null;
  looking_for_new: boolean | null;
  updated_at?: string;
}

export const useCityProfile = () => {
  const {user} = useAuth();
  const userId = user?.id;

  return useQuery<CityProfileData>({
    queryKey: ['cityProfile', userId],
    queryFn: async () => {
      if (!userId)
        return {
          city_duration: null,
          neighborhood_duration: null,
          home_duration: null,
          is_renting: null,
          looking_for_new: null,
        };
      const res = await fetchCityProfile(userId);
      const data = (res as any)?.data?.data || (res as any)?.data;
      return {
        city_duration: data?.city_duration ?? null,
        neighborhood_duration: data?.neighborhood_duration ?? null,
        home_duration: data?.home_duration ?? null,
        is_renting: data?.is_renting ?? null,
        looking_for_new: data?.looking_for_new ?? null,
        updated_at: data?.updated_at,
      };
    },
    enabled: !!userId,
  });
};

export const useUpsertCityProfile = () => {
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['cityProfile', user?.id];

  return useMutation({
    mutationFn: (data: {
      city_duration?: string | null;
      neighborhood_duration?: string | null;
      home_duration?: string | null;
      is_renting?: boolean | null;
      looking_for_new?: boolean | null;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');
      return upsertCityProfile(user.id, data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries(queryKey);
      const previous = queryClient.getQueryData<CityProfileData>(queryKey);
      queryClient.setQueryData<CityProfileData>(queryKey, (old) => ({
        city_duration: data.city_duration ?? old?.city_duration ?? null,
        neighborhood_duration: data.neighborhood_duration ?? old?.neighborhood_duration ?? null,
        home_duration: data.home_duration ?? old?.home_duration ?? null,
        is_renting: data.is_renting ?? old?.is_renting ?? null,
        looking_for_new: data.looking_for_new ?? old?.looking_for_new ?? null,
        updated_at: old?.updated_at,
      }));
      return {previous};
    },
    onError: (_err, _data, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });
};
